/**
 * The back office's map (Back Office Review, wave B): six areas, each a list of pages in
 * groups. The rail draws the areas, the panel draws the active area's pages, Jump (⌘K)
 * searches all of it. One file, so the nav, the palette and the breadcrumb never disagree.
 *
 * `perm` gates a page on a permission the user holds (see `RequirePermission`); pages
 * without one are for anyone signed in.
 *
 * `till: true` marks a page that needs Verlo on the floor to mean anything — a cash-up
 * nobody counted, a live-ops board with no tills on it. A back-office site (one still
 * inside somebody else's EPOS contract, sending us its sales) does not see them;
 * `pageSuitsPlan` below is the one place that decides. Hidden rather than shown empty: a
 * screen that can only ever say "no data" teaches people the product is broken.
 */
import { Sun, BarChart3, Users, UtensilsCrossed, Package, Settings } from 'lucide-react';

export const AREAS = [
  {
    key: 'today', label: 'Today', to: '/back-office', exact: true, icon: Sun,
    groups: [
      { pages: [
        { to: '/back-office', label: 'Today', exact: true, hint: 'Last night, what is due, what needs a decision' },
        { to: '/back-office/pnl', label: 'P&L overview', hint: 'Revenue, costs and profit at a glance' },
      ] },
    ],
  },
  {
    key: 'reports', label: 'Reports', to: '/back-office/reports', icon: BarChart3,
    groups: [
      { label: 'Trading', pages: [
        { to: '/back-office/reports', label: 'Executive', exact: true, perm: 'reports.view_exec', hint: 'Today against last week and the budget' },
        { to: '/back-office/reports/ops/daily-trading', label: 'Daily', perm: 'reports.view_exec', hint: 'A day, hour by hour, against the same day last week' },
        { to: '/back-office/reports/ops/hourly-trading', label: 'Hourly', perm: 'reports.view_exec', hint: 'Sales, covers and labour by hour' },
        { to: '/back-office/reports/ops/weekly-performance', label: 'Weekly', perm: 'reports.view_exec', hint: 'The week, day by day' },
        { to: '/back-office/reports/service-mode', label: 'Service mode', perm: 'reports.view_exec', till: true, hint: 'How each service ran, minute by minute' },
        { to: '/back-office/reports/live-ops', label: 'Live ops', perm: 'reports.view_live_ops', till: true, hint: 'What the tills are doing right now' },
      ] },
      { label: 'Money', pages: [
        { to: '/back-office/reports/pnl', label: 'Profit & loss', perm: 'reports.view_financial', hint: 'The whole story on one page' },
        { to: '/back-office/reports/financial', label: 'Financial', perm: 'reports.view_financial', hint: 'Revenue, VAT, payments, refunds' },
        { to: '/back-office/reports/budget', label: 'Budget', perm: 'reports.view_budget', hint: 'What you expected against what happened' },
        { to: '/back-office/reports/ops/discounts-refunds', label: 'Discounts & refunds', perm: 'reports.view_exec', hint: 'Who gave what away, and why' },
        { to: '/back-office/reports/cash-ups', label: 'Cash-ups', perm: 'reports.view_financial', till: true, hint: 'Every drawer counted, the variances, and the safe' },
        { to: '/back-office/reports/typed-extras', label: 'Typed extras', perm: 'reports.view_financial', till: true, hint: 'Every free-text modifier that carried a price' },
      ] },
      { label: 'Stock & people', pages: [
        { to: '/back-office/reports/stock', label: 'Stock & margin', perm: 'reports.view_stock', hint: 'Cost of sales and margin by item' },
        { to: '/back-office/reports/products', label: 'Products', perm: 'reports.view_exec', hint: 'What sells, what earns' },
        { to: '/back-office/reports/modifiers', label: 'Modifiers', perm: 'reports.view_exec', hint: 'What people change, what the extras earn' },
        { to: '/back-office/reports/promotions', label: 'Promotions', perm: 'reports.view_exec', hint: 'Did the offer work: by rate, item, seller, site' },
        { to: '/back-office/reports/sales-mix', label: 'Sales mix', perm: 'reports.view_exec', hint: 'Trading up or down, month on month' },
        { to: '/back-office/reports/labour', label: 'Labour', perm: 'reports.view_labour', hint: 'Hours, cost and cost as a share of sales' },
        { to: '/back-office/reports/ops/splh', label: 'Sales per labour hour', perm: 'reports.view_exec', hint: 'The one productivity number' },
        { to: '/back-office/reports/staff', label: 'Team', perm: 'reports.view_exec', hint: 'Who sold what' },
      ] },
      { label: 'Tools', pages: [
        { to: '/back-office/reports/comparison', label: 'Compare', perm: 'reports.view_exec', hint: 'Any two periods or sites, side by side' },
        { to: '/back-office/reports/builder', label: 'Report builder', perm: 'reports.view_builder', hint: 'Your own cut of the numbers' },
        { to: '/back-office/reports/dashboards', label: 'Dashboards', perm: 'reports.view_builder', hint: 'Your own page of numbers and charts' },
        { to: '/back-office/reports/library', label: 'All reports', perm: 'reports.view_exec', hint: 'Saved, scheduled and shared reports' },
        { to: '/back-office/reports/ops/monthly-board-pack', label: 'Board pack', perm: 'reports.view_exec', hint: 'The month, ready to print' },
        { to: '/back-office/reports/before-service', label: 'Before Service email', perm: 'reports.view_exec', hint: 'Your email each morning: yesterday, and tonight' },
        { to: '/back-office/reports/schedules', label: 'Schedules', perm: 'reports.view_schedules', hint: 'Reports that send themselves' },
        { to: '/back-office/reports/sites', label: 'Sites', perm: 'reports.view_sites', hint: 'Every site side by side' },
        { to: '/back-office/reports/supplier-reports', label: 'Suppliers', perm: 'reports.view_exec', hint: 'Spend and price movement by supplier' },
      ] },
    ],
  },
  {
    key: 'people', label: 'People', to: '/back-office/team', icon: Users,
    groups: [
      { pages: [
        { to: '/back-office/team', label: 'Team', hint: 'Everyone on the team, who they report to, their PIN' },
        { to: '/back-office/rota', label: 'Rota', hint: 'Who works when' },
        { to: '/back-office/timesheets', label: 'Timesheets', hint: 'Hours worked against hours planned' },
        { to: '/back-office/labour-costs', label: 'Labour costs', perm: 'reports.view_labour', hint: 'What the team costs, by day and role' },
      ] },
    ],
  },
  {
    key: 'menu', label: 'Menu', to: '/back-office/menu', icon: UtensilsCrossed,
    groups: [
      { pages: [
        { to: '/back-office/menu', label: 'Items', hint: 'Everything the till sells: price, cost, margin', exact: true },
        { to: '/back-office/menu/structure', label: 'Structure', hint: 'Sections, and the order the till shows them' },
        { to: '/back-office/menu/import', label: 'Import', perm: 'bridge.import', hint: 'Bring a menu in from a spreadsheet' },
        { to: '/back-office/modifiers', label: 'Modifiers', hint: 'Sizes, extras and options' },
        { to: '/back-office/promotions', label: 'Promotions', hint: 'Offers the till applies on its own' },
        { to: '/back-office/recipes', label: 'Recipes', hint: 'What goes into each dish, and what it costs' },
      ] },
    ],
  },
  {
    key: 'stock', label: 'Stock', to: '/back-office/stock', icon: Package,
    groups: [
      { label: 'On hand', pages: [
        { to: '/back-office/stock', label: 'Stock levels', hint: 'What is below par, and what to order' },
        { to: '/back-office/stock-takes', label: 'Stock takes', hint: 'Count what is there' },
        { to: '/back-office/transfers', label: 'Transfers', hint: 'Stock moved between sites' },
        { to: '/back-office/waste', label: 'Waste', hint: 'What went in the bin, and what it cost' },
      ] },
      { label: 'Buying', pages: [
        { to: '/back-office/orders', label: 'Orders', hint: 'What you buy from suppliers: drafted, sent, received' },
        { to: '/back-office/deliveries', label: 'Deliveries', hint: 'What arrived, against what was ordered' },
        { to: '/back-office/local-purchases', label: 'Local purchases', hint: 'Cash-and-carry and one-offs' },
        { to: '/back-office/suppliers', label: 'Suppliers', hint: 'Who you buy from', exact: true },
        { to: '/back-office/suppliers/import', label: 'Import suppliers', perm: 'bridge.import', hint: 'Bring your supplier book in from a spreadsheet' },
      ] },
      { label: 'Sales', pages: [
        { to: '/back-office/sales', label: 'Sales', hint: 'Every order, searchable', exact: true },
        { to: '/back-office/sales/import', label: 'Import sales', perm: 'bridge.import', hint: 'Bring takings in from another till' },
      ] },
    ],
  },
  {
    key: 'admin', label: 'Admin', to: '/back-office/settings', icon: Settings,
    groups: [
      { label: 'The business', pages: [
        { to: '/back-office/settings', label: 'Company', hint: 'Name, currency, tax, the financial calendar' },
        { to: '/back-office/sites', label: 'Sites', hint: 'Your venues' },
        // Read on `bridge.view`, which a manager holds; the ticking needs
        // `bridge.configure`, which they do not, and the page goes read-only for them
        // rather than 403-ing a click. Knowing what is outstanding is most of its value.
        { to: '/back-office/go-live', label: 'Getting live', perm: 'bridge.view', hint: 'The fifteen things a venue needs before it can trade' },
        { to: '/back-office/accounts', label: 'Accounts', hint: 'Who can sign in, and what they can see' },
        // The manuals (Martin, 27 Oct): the guide's two books, each chapter opening on the guide's site.
        { to: '/back-office/guide', label: 'The Verlo Guide', hint: 'How to use Verlo: the Back Office and Front of House books' },
      ] },
      // The room and the kitchen (Back office sweep, Oct 2026: D11): how the restaurant is
      // laid out, and which dishes go through which station.
      { label: 'The restaurant', pages: [
        { to: '/back-office/floor-plan', label: 'Floor plan', perm: 'floor.edit_floorplan', till: true, hint: 'Each site\'s tables, areas and the room they\'re in' },
        { to: '/back-office/kitchen-stations', label: 'Kitchen stations', perm: 'settings.view', till: true, hint: 'The stations food goes through, and which dishes go where' },
        // The one entry a back-office site *does* see in this group, in place of the two
        // above. The upsell belongs where the gap is, said once and quietly, rather than
        // scattered as disabled rows across every till-shaped screen.
        { to: '/back-office/front-of-house', label: 'Add the floor', backOfficeOnly: true, hint: 'Run service on Verlo too, when your till contract ends' },
      ] },
      { label: 'The tills', pages: [
        { to: '/node', label: 'Verlo Node', till: true, hint: 'The tills, their sync and their health' },
      ] },
      { label: 'Records', pages: [
        { to: '/back-office/audit', label: 'Audit log', perm: 'settings.view', hint: 'Every login, override and permission denial' },
      ] },
    ],
  },
];

/**
 * Does this page belong to a site on this plan? The till-shaped pages (`till: true`) are
 * for a venue running Verlo on the floor; the upsell row (`backOfficeOnly: true`) is for
 * one that is not. Pure, and the single definition — the sidebar, the area rail and Jump
 * all ask it, so a page cannot be hidden from the nav and still reachable by ⌘K.
 *
 * `hasTill` defaults to true: nothing is hidden while the site list is still loading, and
 * nothing changes for a site whose plan was never set.
 */
export const pageSuitsPlan = (page, hasTill = true) => {
  if (page.till && !hasTill) return false;
  if (page.backOfficeOnly && hasTill) return false;
  return true;
};

/** Every page, flat, with its area — for Jump and the breadcrumb. */
export const PAGES = AREAS.flatMap((area) => area.groups.flatMap((g) => g.pages.map((p) => ({ ...p, area, group: g.label || null }))));

/** The area a path belongs to (longest matching page wins). */
export function areaFor(pathname) {
  const hit = PAGES.filter((p) => (p.exact ? pathname === p.to : pathname === p.to || pathname.startsWith(p.to + '/')))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return hit ? { area: hit.area, page: hit } : { area: AREAS[0], page: null };
}
